<template>
  <div>
    <BaseHeader />
    <ConnectionError v-if="isConnectionError" :retry="invokeLastRequest" :retry-failed="showGeneralError" />
    <BaseLayout>
      <LoadingScreen v-if="loading" />
      <NuxtPage />
    </BaseLayout>
    <BaseFooter />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import type { AxiosError } from 'axios'
import axios from 'axios'
import { setLocale } from 'yup'
import { useHead } from '@unhead/vue'
import { showError } from '#app'
import VuePiwikPro from '@piwikpro/vue-piwik-pro'
import BaseLayout from '~/base/components/layout/Layout.vue'
import BaseHeader from '~/base/components/header/Header.vue'
import BaseFooter from '~/base/components/footer/Footer.vue'
import ConnectionError from '@/base/components/connectionError/ConnectionError.vue'
import LoadingScreen from '~/base/components/spinner/Spinner.vue'
import { useAppStore } from '~/base/stores/app'
import lostConnection from '@/base/assets/illustrations/lost-connection.svg'
import '~/base/assets/css/sanitize.css'

useHead({
  title: 'ista Express Service',
  noscript: [
    {
      children:
        'Der ista Express Service funktioniert leider nicht, wenn JavaScript deaktiviert ist. Bitte aktivieren Sie JavaScript.'
    }
  ],
  script: [
    {
      src: 'https://app.usercentrics.eu/browser-ui/latest/loader.js',
      id: 'usercentrics-cmp',
      'data-settings-id': 'rEK3jtYuc',
      async: true
    }
  ],
  link: [
    { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
    { rel: 'icon', type: 'image/png', href: '/favicon.svg' }
  ]
})
const store = useAppStore()

const loading = computed((): boolean => {
  return store.requestState.loading
})

const isConnectionError = computed(() => {
  return (
    getError.value &&
    ((!isOnline.value && store.isApiError(getError.value)) ||
      (isOnline.value &&
        store.isApiError(getError.value) &&
        [502, 503, 504].includes(getError.value.response?.status ?? 0)))
  )
})

const isOnline = computed((): boolean => {
  return store.isOnline
})

const getError = computed((): AxiosError | unknown => {
  return store.requestState.error
})

const invokeLastRequest = async () => {
  try {
    console.log('Try to send the last request again.')
    const error = store.requestState.error
    if (error) {
      await axios.request(error.config)
      await store.requestSucceeded()
    }
  } catch (e) {
    await store.requestFailed(e)
  }
}

const showGeneralError = async (): Promise<void> => {
  await store.requestFailed(null)
  showError('')
}

onMounted(() => {
  try {
    const piwikProContainerId = useRuntimeConfig().public.piwikProContainerId
    const piwikProContainerUrl = useRuntimeConfig().public.piwikProContainerUrl
    VuePiwikPro.initialize(piwikProContainerId, piwikProContainerUrl)
  } catch (error) {
    console.error('Error initializing Piwik:', error)
  }
})

onMounted(() => {
  setInterval(() => {
    store.updateOnlineState(navigator.onLine)
  }, 1000)
  preLoadImage(lostConnection)
  setLocale({
    mixed: {
      required: 'Dieses Feld ist ein Pflichtfeld'
    },
    string: {
      email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
    }
  })
})

// TODO could probably be removed, nuxt has its own error handling
watch(getError, (newValue) => {
  if (newValue !== null && !isConnectionError.value) {
    showGeneralError()
  }
})

const preLoadImage = (url: string) => {
  const img = new Image()
  img.src = url
}
</script>
